import {
  ClockCircleFilled,
  CloseCircleFilled,
  CloseCircleOutlined,
  DashboardFilled,
  DeleteColumnOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
  WarningFilled,
} from '@ant-design/icons'
import { humanReadableSecondDifferenceFromToday } from '@utils/general'
import { formatters } from '@utils/intl/dateTimeFormatters'
import moment from 'moment'
import CardError from '@assets/CustomIcons/credit-card-off2.png'
import { IconDropletX } from '@tabler/icons-react'

type Issue = {
  id: number
  type: keyof typeof DRIVER_SCHEDULE_ISSUES
  message: string
}

export const shouldShowLink = (trip, schedule, tripIndex) => {
  const isLast = tripIndex < schedule.trips.length && schedule.trips[tripIndex + 1]?.order_id === null
  const isComplete = trip.state === 'complete'
  return isLast && !isComplete
}
export const getTripErrors = (trip, config, schedule) => {
  const retainTime = moment(trip.dispatch_window_start)
  const runoutTime = moment(trip.dispatch_window_end)
  const tripEnd = moment(trip.estimated_end)
  const diffToRetainMin = Math.abs(moment.duration(tripEnd.diff(retainTime)).asMinutes())
  const diffToRunout = Math.abs(moment.duration(tripEnd.diff(runoutTime)).asMinutes())
  const retainBuffer = config?.trip_grid_retain_buffer_minutes
  const runoutBuffer = config?.trip_grid_runout_buffer_minutes
  const errors = []
  const unavailableWindowDrops = trip.drops.filter(
    (d) => Array.isArray(d.unavailable_hour) && d.unavailable_hour.some((hour) => hour.start_hour && hour.end_hour)
  )
  if (trip.flags.some((flag) => flag.type === 'compliance_hold')) {
    errors.push(complianceHold(trip.flags.find((flag) => flag.type === 'compliance_hold')))
  }
  if (unavailableWindowDrops && unavailableWindowDrops.length) {
    const dropsInsideWindow = unavailableWindowDrops.reduce((acc, drop) => {
      const matchingWindows = drop.unavailable_hour.filter((window) => {
        const windowStart = moment(window.start_hour)
        const windowEnd = moment(window.end_hour)
        return tripEnd.isAfter(windowStart) && tripEnd.isBefore(windowEnd)
      })
      if (matchingWindows.length) {
        acc.push({
          ...drop,
          unavailable_hour: matchingWindows,
        })
      }

      return acc
    }, [])

    if (dropsInsideWindow && dropsInsideWindow.length) {
      errors.push(...isInUnavailableWindow(dropsInsideWindow, errors))
    }
  }

  if (tripEnd.isBefore(retainTime)) {
    errors.push(retainError(diffToRetainMin, trip?.has_call_in_sites))
  } else if (retainBuffer && diffToRetainMin <= retainBuffer) {
    errors.push(retainWarning(diffToRetainMin, trip?.has_call_in_sites))
  }

  if (tripEnd.isAfter(runoutTime)) {
    errors.push(runoutError(diffToRunout, trip?.has_call_in_sites))
  } else if (runoutBuffer && diffToRunout <= runoutBuffer) {
    errors.push(runoutWarning(diffToRunout, trip?.has_call_in_sites))
  }

  if (trip.card_alerts?.length > 0) {
    trip.card_alerts.forEach((alert) => {
      errors.push(cardingHold(alert, schedule.driverId))
    })
  }

  if (errors.length === 0 && trip.issues?.length === 0) {
    const message = trip?.has_call_in_sites ? 'On Time' : 'Great Fit!'
    errors.push({
      message,
      status: 'success',
      icon: <ClockCircleFilled />,
    })
  }

  trip.issues?.forEach((issue) => {
    errors.push(driverScheduleIssue(issue, schedule.id))
  })

  return errors.sort((a, b) => (a.severity > b.severity ? 1 : -1))
}

const complianceHold = () => {
  return {
    message: 'Projected Delivery Past UST Expiration',
    status: 'error',
    icon: <InfoCircleOutlined />,
    severity: 0,
    type: 'compliance_hold',
  }
}
const retainError = (diffToRetain, hasCallIns) => {
  const retainSeconds = diffToRetain * 60
  const description = hasCallIns
    ? `Scheduled drop time is  ${humanReadableSecondDifferenceFromToday(
        retainSeconds,
        false
      )} before delivery window start.`
    : `Drop time is ${humanReadableSecondDifferenceFromToday(retainSeconds, false)} before retain.`
  return {
    message: hasCallIns ? 'Before Delivery Window' : 'NO FIT',
    status: 'error',
    description,
    icon: <DashboardFilled />,
    severity: 1,
    type: 'retain',
  }
}

const isInUnavailableWindow = (unavailableDrops) => {
  return unavailableDrops.map((d) => ({
    message: `SITE UNAVAILABLE (${d.location_name})`,
    status: 'error',
    description: `Drop time is within ${formatters.TIME(moment(d.unavailable_hour[0].start_hour))} - ${formatters.TIME(
      moment(d.unavailable_hour[0].end_hour)
    )}.`,
    icon: <DeleteColumnOutlined />,
    severity: 2,
    type: 'unavailable',
  }))
}
const runoutWarning = (runoutDiff, hasCallIns) => {
  const runoutSeconds = runoutDiff * 60
  const description = hasCallIns
    ? `Scheduled drop time is within ${humanReadableSecondDifferenceFromToday(
        runoutSeconds,
        false
      )} of delivery window end.`
    : `Drop time is within ${humanReadableSecondDifferenceFromToday(runoutSeconds, false)} of estimated tank runout.`
  return {
    message: hasCallIns ? 'After Delivery Window' : 'POTENTIAL RUNOUT',
    status: 'warning',
    description,
    icon: <CloseCircleOutlined />,
    severity: 3,
    type: 'runout',
  }
}
const runoutError = (runoutDiff, hasCallIns) => {
  const runoutSeconds = runoutDiff * 60
  const description = hasCallIns
    ? `Scheduled drop time is ${humanReadableSecondDifferenceFromToday(
        runoutSeconds,
        false
      )} after delivery window end.`
    : `Drop time is ${humanReadableSecondDifferenceFromToday(runoutSeconds, false)} after tank has run out.`
  return {
    message: hasCallIns ? 'After Delivery Window' : 'RUNOUT',
    status: 'error',
    description,
    icon: <CloseCircleFilled />,
    severity: 3,
    type: 'runout',
  }
}
const retainWarning = (diffToRetain, hasCallIns) => {
  const retainSeconds = diffToRetain * 60
  const description = hasCallIns
    ? `Scheduled drop time is within ${humanReadableSecondDifferenceFromToday(
        retainSeconds,
        false
      )} of delivery window start.`
    : `Drop time is within ${humanReadableSecondDifferenceFromToday(retainSeconds, false)} of retain.`
  return {
    message: hasCallIns ? 'Before Delivery Window' : 'POTENTIAL NO FIT',
    status: 'warning',
    description,
    icon: <DashboardFilled />,
    severity: 4,
    type: 'retain',
  }
}

const DRIVER_SCHEDULE_ISSUES = {
  'product incompatibility': {
    title: 'Product Incompatibility',
    status: 'warning',
    icon: <IconDropletX />,
    resolvable: false,
  },
  'compartment product incompatibility': {
    title: 'Compartment Incompatibility',
    status: 'error',
    icon: <IconDropletX />,
    resolvable: false,
  },
  'product mismatch': {
    title: 'Product Mismatch',
    status: 'error',
    icon: <ExclamationCircleFilled />,
    resolvable: true,
  },
  'volume discrepancy': {
    title: 'Volume Discrepancy',
    status: 'warning',
    icon: <WarningFilled />,
    resolvable: true,
  },
  'supplier mismatch': {
    title: 'Supplier Mismatch',
    status: 'warning',
    icon: <WarningFilled />,
    resolvable: true,
  },
  'pump mismatch': {
    title: 'Pump Missing',
    status: 'warning',
    icon: <WarningFilled />,
    resolvable: false,
  },
} as const

const driverScheduleIssue = (issue: Issue, id: number) => {
  return {
    id: issue.id,
    driverScheduleId: id,
    message: DRIVER_SCHEDULE_ISSUES?.[issue?.type]?.title || '',
    status: DRIVER_SCHEDULE_ISSUES?.[issue?.type]?.status || '',
    description: issue.message,
    icon: DRIVER_SCHEDULE_ISSUES?.[issue?.type]?.icon || '',
    severity: status === 'error' ? 3 : 4,
    type: issue.type,
    resolvable: DRIVER_SCHEDULE_ISSUES?.[issue?.type]?.resolvable || false,
  }
}

const cardingHold = (alert, id) => {
  const item = { ...alert, driver_id: id }
  return {
    message: 'Card Hold',
    status: 'error',
    severity: 5,
    type: 'card_alert',
    item,
    icon: <img src={CardError} alt={'card error image'} style={{ height: '13px' }} />,
  }
}
